import { FormFieldClickable } from "@flip/MobiusOverrides/FormField/FormField";
import { InputStyle } from "@flip/MobiusOverrides/TextField";
import SearchInput, { SearchInputStyles } from "@flip/Overrides/Widgets/Header/SearchInput";
import { PlaceholderSelector } from "@flip/XDL/PreStyle/formField";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { IconWrapper } from "@insite/mobius/Icon";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as cssLinter from "css";
import React from "react";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    // XNG-Changes
    bannerMode = "bannerMode",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.bannerMode]: boolean;
    };
}

export interface HeaderSearchInputStyles {
    searchInputStyles?: SearchInputStyles;
}

export const searchInputStyles: HeaderSearchInputStyles = {};

// XNG-Changes
export const largeSearchInputStyles: HeaderSearchInputStyles = {
    searchInputStyles: {
        input: {
            iconClickableProps: {
                iconPosition: "right",
            },
            cssOverrides: {
                formInputWrapper: css`
                    && ${InputStyle} {
                        height: 78px;
                        box-shadow: inset 0 1px 0 0 rgba(0, 0, 0, 0.1);
                        background: ${color("common.ui15")};

                        color: ${color("text")};

                        &,
                        &:focus {
                        }

                        ${xScreen("lte", "md")} {
                            height: 58px;
                            padding: 18px 60px 18px 18px;
                            font-size: 18px;
                            line-height: 20px;
                        }

                        &,
                        &:hover,
                        &:focus {
                            border: 3px solid ${color("primary")};
                            padding: 25px 80px 23px 15px;
                            border-radius: 8px;
                            font-size: 26px;
                            font-weight: ${fontConfig.weight.regular};
                            line-height: 29px;

                            ${xScreen("lte", "md")} {
                                padding-left: 18px;
                            }
                        }

                        ${PlaceholderSelector} {
                            font-size: 26px;
                            line-height: 29px;
                            font-style: italic;
                            color: ${color("common.ui4")};
                            
                            ${xScreen("lte", "md")} {
                                font-size: 18px;
                                line-height: 20px;
                            }
                        }
                    }

                    && ${FormFieldClickable} {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: ${color("primary")};
                        height: 78px;
                        width: 78px;
                        border-top-right-radius: 8px;
                        border-bottom-right-radius: 8px;

                        ${xScreen("lte", "md")} {
                            height: 58px;
                            width: 58px;
                        }

                        ${xScreen("lt", "sm")} {
                            background: none;
                        }

                        ${IconWrapper} {
                            background: none;
                            padding: 0;
                            position: relative;
                            top: auto;
                            right: auto;
                            margin: auto;

                            &,
                            svg {
                                height: 36px;
                                width: 36px;
                                ${xScreen("lt", "sm")} {
                                    height: 26px;
                                    width: 26px;
                                }
                            }

                            svg {
                                color: ${color("common.white")};
                                ${xScreen("lt", "sm")} {
                                    color: ${color("primary")};
                                }
                            }
                        }
                    }
                `,
            },
        },
    },
};

const defaultCustomCss = `.search-input{
}

.popover{
}

.autocomplete-wrapper{
}

.autocomplete-column-wrapper{
}

.autocomplete-categories-header{
}

.autocomplete-categories-item-text{
}

.autocomplete-categories-item-link{
}

.autocomplete-categories-link{
}

.autocomplete-brands-header{
}

.autocomplete-brands-item-text{
}

.autocomplete-brands-item-link{
}

.autocomplete-brands-link{
}

.autocomplete-content-header{
}

.autocomplete-content-link{
}

.autocomplete-products-header{
}

.autocomplete-products-container{
}

.autocomplete-products-image-grid-item{
}

.autocomplete-products-image{
}

.autocomplete-products-info-grid-item{
}

.autocomplete-products-link{
}

.autocomplete-products-erp-number{
}`;

export const HeaderSearchInput: React.FC<OwnProps & { isAnimated?: boolean; left?: number; top?: number }> = ({
    id,
    fields,
    isAnimated = false,
    // XNG-Changes
    left,
    top,
}) => {
    const styles = useMergeStyles("headerSearchInput", fields.bannerMode ? largeSearchInputStyles : searchInputStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* XNG-Changes: add left,top property to be used in Compact Header */}
            <SearchInput
                id={`headerSearch-${uniqueId()}`}
                isAnimated={isAnimated}
                left={left}
                top={top}
                extendedStyles={styles.searchInputStyles}
                hideOverlay={fields.bannerMode}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderSearchInput,
    definition: {
        group: "Header",
        displayName: "Search Input",
        // allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.bannerMode,
                displayName: "Banner Mode - Large size",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
